/**
 * LMS — Pont Didomi → Shopify Customer Privacy API
 * ------------------------------------------------
 * Didomi est la CMP du site : c'est elle qui collecte la décision de l'utilisateur.
 * Shopify, lui, pilote ses propres traceurs (Web Pixels du checkout, cookies
 * `_shopify_*`, canaux de vente natifs, CAPI Shopify-side) via la **Customer Privacy
 * API** — un système de consentement totalement distinct de la CMP.
 *
 * Sans pont entre les deux, sur cette boutique (région `FRARA`, `getRegulation() === 'GDPR'`,
 * `isRegulationEnforced() === true`), `currentVisitorConsent()` reste
 * `{marketing:'', analytics:'', preferences:'', sale_of_data:''}` : aucune décision n'est
 * jamais enregistrée côté Shopify, donc **tous les Web Pixels restent bloqués en permanence**,
 * même pour un visiteur qui a tout accepté dans Didomi.
 *
 * Ce script traduit la décision Didomi en deux temps, pour rester lisible et auditable :
 *
 *   1. statut Didomi (purposes + vendors)  →  les 7 signaux Google Consent Mode v2
 *   2. signaux Google                      →  les 4 catégories Shopify
 *
 * L'étape 1 n'invente rien : elle reprend les purposes que les **vendors Google déclarés
 * dans la notice Didomi** exigent réellement (cf. GOOGLE_SIGNALS ci-dessous). Si la notice
 * Didomi évolue (ajout d'un purpose au vendor Google), c'est cette table qu'il faut mettre
 * à jour — et elle seule.
 *
 * ⚠️ Écriture uniquement sur décision utilisateur : Shopify demande explicitement de ne
 * jamais appeler `setTrackingConsent()` automatiquement, mais seulement quand le visiteur
 * a réellement tranché. On s'appuie donc sur `Didomi.shouldUserStatusBeCollected()` : tant
 * qu'elle renvoie `true` (aucune décision prise / à re-collecter), on n'écrit rien et
 * Shopify reste en « pas de décision », c'est-à-dire bloquant.
 *
 * ⚠️ Timing : le SDK Didomi est chargé en différé (`type="text/lazyload"`, cf.
 * `snippets/observer-CFH.liquid`) — il n'existe qu'après la première interaction. On passe
 * donc par la file d'attente `window.didomiOnReady`, qui fonctionne avant le chargement du SDK.
 *
 * Chargé globalement depuis `layout/theme.liquid`. No-op si Didomi ou l'API Shopify
 * sont absents (preview, environnement sans CMP…).
 *
 * Debug : ajouter `?lms_consent_debug=1` à l'URL pour tracer le mapping en console.
 */
(() => {
  'use strict';

  /**
   * Étape 1 — Purposes/vendors Didomi requis pour accorder chaque signal Google Consent Mode v2.
   *
   * ## La règle : purpose ET vendor, jamais l'un sans l'autre
   *
   * Didomi (modèle TCF) pose deux questions séparées à l'utilisateur :
   *   - les **purposes** = À QUOI on a le droit (« déposer des cookies », « mesurer la
   *     performance du contenu »…). Ils sont **partagés par tous les vendors** de la notice :
   *     `cookies` et `measure_content_performance` sont aussi déclarés par Hotjar, Klaviyo,
   *     Pinterest, Bing Ads, iAdvize…
   *   - les **vendors** = À QUI on donne ce droit (Google Advertising Products,
   *     Google Analytics Products…).
   *
   * Un traitement n'est licite que si les DEUX sont accordés. D'où le ET, et ses deux
   * conséquences asymétriques :
   *   - vendor accordé + purpose refusé → **refusé** : l'utilisateur accepte Google mais pas
   *     le dépôt de cookies. Google n'a pas le droit de stocker.
   *   - purpose accordé + vendor refusé → **refusé** : l'utilisateur a accepté « mesurer la
   *     performance du contenu » — mais peut-être pour Klaviyo, pas pour Google, qu'il a
   *     décoché. Sans le test du vendor, on accorderait à Google un consentement donné à
   *     quelqu'un d'autre.
   *
   * `security_storage` n'est pas optionnel (auth, anti-fraude) : toujours accordé.
   *
   * ## Les exigences listées ici
   *
   * Elles reprennent les vendors Google déclarés dans la notice Didomi et les purposes qu'ils
   * exigent (relevés dans `Didomi.getConfig().app.vendors`) :
   *   - `google` (Google Advertising Products) → cookies, create_ads_profile, select_personalized_ads
   *     (+ intérêt légitime : select_basic_ads, measure_ad_performance, market_research, improve_products)
   *   - `c:googleana-4TXnJigR` (Google Analytics Products) → cookies, measure_content_performance
   *
   * ⚠️ Les purposes sont répartis **par signal**, pas recopiés en bloc depuis le vendor : c'est
   * ce qui garde `ad_storage`, `ad_user_data` et `ad_personalization` indépendants, comme le
   * veut Consent Mode. Un refus de la pub personnalisée doit couper `ad_personalization` sans
   * couper `ad_storage` (cf. `vendorEnabled` plus bas, qui lit le statut BRUT du vendor).
   *
   * ⚠️ Les ids de vendors sont ceux de l'API `getUserStatusForVendor` : préfixe `c:` pour les
   * vendors custom de la notice, sans préfixe pour les vendors du référentiel Didomi.
   *
   * @type {Record<string, {purposes: string[], vendors: string[]} | null>}
   */
  const GOOGLE_SIGNALS = {
    ad_storage: { purposes: ['cookies'], vendors: ['google'] },
    ad_user_data: { purposes: ['cookies', 'measure_ad_performance'], vendors: ['google'] },
    ad_personalization: {
      purposes: ['create_ads_profile', 'select_personalized_ads'],
      vendors: ['google'],
    },
    analytics_storage: {
      purposes: ['cookies', 'measure_content_performance'],
      vendors: ['c:googleana-4TXnJigR'],
    },
    functionality_storage: { purposes: ['cookies'], vendors: [] },
    personalization_storage: {
      purposes: ['create_content_profile', 'select_personalized_content'],
      vendors: [],
    },
    security_storage: null, // toujours granted
  };

  /**
   * Étape 2 — Catégories Shopify dérivées des signaux Google.
   *
   * - `analytics`    ← `analytics_storage`   (GA4, Shop analytics, Web Pixels de mesure)
   * - `marketing`    ← `ad_storage`          (pixels pub, canaux de vente, CAPI)
   * - `preferences`  ← `functionality_storage` (langue, devise, préférences stockées)
   *
   * `sale_of_data` est volontairement absent : la boutique est en région GDPR
   * (`saleOfDataRegion() === false`) et Shopify recommande de ne le poser que dans un vrai
   * parcours d'opt-out initié par le client. L'ajouter ici reviendrait à écrire une décision
   * CCPA que l'utilisateur n'a jamais prise. Le payload étant partiel, Shopify laisse la clé
   * inchangée.
   *
   * @type {Record<string, string>}
   */
  const SHOPIFY_CONSENT = {
    analytics: 'analytics_storage',
    marketing: 'ad_storage',
    preferences: 'functionality_storage',
  };

  /** Feature Shopify à charger pour disposer de `Shopify.customerPrivacy`. */
  const CONSENT_FEATURE = [{ name: 'consent-tracking-api', version: '0.1' }];

  /** Trace le mapping en console si `?lms_consent_debug=1`. */
  const DEBUG = new URLSearchParams(window.location.search).has('lms_consent_debug');

  /** @type {Promise<object> | null} Chargement de l'API Shopify (mémoïsé). */
  let apiPromise = null;

  /** @type {Promise<void>} Sérialise les écritures pour éviter les appels concurrents. */
  let queue = Promise.resolve();

  /**
   * @param {...unknown} args
   */
  const log = (...args) => {
    if (DEBUG) console.info('[lms-didomi-customer-privacy]', ...args);
  };

  /**
   * Charge `Shopify.customerPrivacy` à la demande (une seule fois).
   * @returns {Promise<object>} L'API Customer Privacy.
   */
  const loadCustomerPrivacy = () => {
    if (apiPromise) return apiPromise;

    apiPromise = new Promise((resolve, reject) => {
      const api = window.Shopify?.customerPrivacy;
      if (api?.setTrackingConsent) {
        resolve(api);
        return;
      }

      if (typeof window.Shopify?.loadFeatures !== 'function') {
        reject(new Error('Shopify.loadFeatures indisponible'));
        return;
      }

      window.Shopify.loadFeatures(CONSENT_FEATURE, (error) => {
        if (error || !window.Shopify.customerPrivacy?.setTrackingConsent) {
          reject(error || new Error('consent-tracking-api non chargée'));
          return;
        }
        resolve(window.Shopify.customerPrivacy);
      });
    });

    // Un échec ne doit pas rester mémoïsé : la tentative suivante doit pouvoir réessayer.
    apiPromise.catch(() => {
      apiPromise = null;
    });

    return apiPromise;
  };

  /**
   * Calcule les 7 signaux Google Consent Mode v2 à partir du statut Didomi courant.
   * @param {object} Didomi - L'API Didomi (déjà prête).
   * @returns {Record<string, boolean>} Signal → accordé (true) / refusé (false).
   */
  const computeGoogleSignals = (Didomi) => {
    const purposes = Didomi.getCurrentUserStatus()?.purposes || {};

    // Un purpose/vendor absent de la notice est traité comme refusé : on ne présume
    // jamais un consentement qui n'a pas été demandé.
    const purposeEnabled = (id) => purposes[id]?.enabled === true;

    // Statut BRUT du vendor (« l'utilisateur a-t-il coché Google ? »), volontairement pas
    // `getCurrentUserStatus().vendors[id].enabled` ni `getUserStatusForVendorAndLinkedPurposes()` :
    // ces deux-là valent déjà « vendor ET la TOTALITÉ de ses purposes ». Les utiliser ferait
    // tomber `ad_storage` et `ad_user_data` dès que l'utilisateur refuse la pub personnalisée
    // (purpose du vendor `google`), alors que ces deux signaux n'en dépendent pas — vérifié en
    // runtime. Le croisement avec les purposes se fait ici, signal par signal.
    const vendorEnabled = (id) => Didomi.getUserStatusForVendor(id) === true;

    return Object.entries(GOOGLE_SIGNALS).reduce((signals, [signal, requirements]) => {
      signals[signal] = requirements
        ? requirements.purposes.every(purposeEnabled) && requirements.vendors.every(vendorEnabled)
        : true;
      return signals;
    }, /** @type {Record<string, boolean>} */ ({}));
  };

  /**
   * Traduit les signaux Google en payload Customer Privacy.
   * @param {Record<string, boolean>} signals
   * @returns {Record<string, boolean>} Payload `setTrackingConsent`.
   */
  const toShopifyConsent = (signals) =>
    Object.entries(SHOPIFY_CONSENT).reduce((payload, [category, signal]) => {
      payload[category] = signals[signal] === true;
      return payload;
    }, /** @type {Record<string, boolean>} */ ({}));

  /**
   * L'état Shopify reflète-t-il déjà ce payload ? Évite une écriture (et un aller-retour
   * serveur + un event `visitorConsentCollected`) inutile à chaque page vue.
   * @param {object} api - `Shopify.customerPrivacy`.
   * @param {Record<string, boolean>} payload
   * @returns {boolean}
   */
  const alreadyApplied = (api, payload) => {
    const current = api.currentVisitorConsent();
    return Object.entries(payload).every(([key, value]) => current[key] === (value ? 'yes' : 'no'));
  };

  /**
   * Écrit la décision Didomi dans Shopify. Les appels sont sérialisés : Didomi peut émettre
   * plusieurs `consent.changed` rapprochés (ex. « tout accepter » puis réglage fin).
   * @param {object} Didomi - L'API Didomi (déjà prête).
   * @param {string} reason - Origine de la synchro, pour le debug.
   */
  const sync = (Didomi, reason) => {
    queue = queue.then(async () => {
      // Aucune décision utilisateur → on laisse Shopify en « indéterminé » (= bloquant).
      if (Didomi.shouldUserStatusBeCollected()) {
        log('décision Didomi non collectée, rien à écrire', { reason });
        return;
      }

      const signals = computeGoogleSignals(Didomi);
      const payload = toShopifyConsent(signals);
      log('mapping', { reason, signals, payload });

      try {
        const api = await loadCustomerPrivacy();

        if (alreadyApplied(api, payload)) {
          log('déjà synchronisé, aucune écriture', payload);
          return;
        }

        await new Promise((resolve, reject) => {
          api.setTrackingConsent(payload, (response) => {
            if (response?.error) reject(new Error(response.error));
            else resolve();
          });
        });

        log('consentement Shopify mis à jour', api.currentVisitorConsent());
      } catch (error) {
        // Échec silencieux côté visiteur : Shopify reste sur son état précédent
        // (donc bloquant tant qu'aucune décision n'a été enregistrée).
        console.warn('[lms-didomi-customer-privacy] synchronisation impossible', error);
      }
    });
  };

  window.didomiOnReady = window.didomiOnReady || [];
  window.didomiOnReady.push((Didomi) => {
    // 1. État au chargement : rejoue la décision déjà prise (visiteur de retour, page 2+).
    sync(Didomi, 'didomiOnReady');

    // 2. Toute modification ultérieure : bannière, modale de préférences, lien « Cookies »
    //    du footer, retrait du consentement… `consent.changed` couvre tous ces cas.
    Didomi.on('consent.changed', () => sync(Didomi, 'consent.changed'));
  });
})();
